import { timer } from '@/utils/helpers'

import { AdSenseScriptFunctions } from './loadAdSenseScript'

/**
 * Never rejects since preloading is optional.
 * According to the documentation:
 * - preloadAdBreaks only takes effect on the first call
 * - calling preloadAdBreaks is desirable but not mandatory
 *
 * Calling it again results in an error
 * "[Ad Placement API] 'adConfig' was already called to set 'preloadAdBreaks' with value 'on'",
 * therefore the calling code needs to check if we've called this function before
 */
export const preloadFirstAdBreak = (
    adSenseScriptFunctions: AdSenseScriptFunctions,
    {
        abortController: parentAbortController,
        timeoutMs,
    }: {
        abortController: AbortController
        timeoutMs: number
    },
): Promise<void> => {
    const { adConfig } = adSenseScriptFunctions

    const timeoutAbortController = new AbortController()
    parentAbortController.signal.addEventListener('abort', () => {
        timeoutAbortController.abort()
    })

    // The onReady callback is not guaranteed by AdSense, they themselves recommend using a timeout
    // https://developers.google.com/ad-placement/docs/manual-sequence
    return Promise.race([
        new Promise<void>((resolve) => {
            // We pass a flag indicating that sound playback is possible
            // AdSense itself checks if sound can be played and if necessary
            // shows a popup to the user like "Turn on volume?" before the ad
            adConfig({
                sound: 'on',
            })

            // Start the loading process and wait for the callback, if it's not called - we still try to show the ad
            adConfig({
                preloadAdBreaks: 'on',
                onReady: () => {
                    resolve()
                    timeoutAbortController.abort()
                },
            })

            parentAbortController.signal.addEventListener('abort', () => {
                resolve()
            })
        }),
        timer(timeoutMs, {
            abortSignal: timeoutAbortController.signal,
        }),
    ])
}
